/**
 * Single source of truth for the two sizes every input in `components/form/inputs`
 * supports.
 */
type InputSize = {
	/** Height + type scale. Owns the rendered height of the field. */
	frame: string;
	/**
	 * Type scale on its own, for text that renders outside the frame — dropdown
	 * option lists are portaled, so they inherit nothing from the control.
	 */
	text: string;
	paddingX: string;
	paddingY: string;
	/** Prefix/suffix slabs (`$`, `USD`) that sit flush inside the frame. */
	affix: string;
	/** Leading icon well — calendar, map pin. Spec: 12px inset + 16px icon + 8px gap. */
	gutter: string;
	/** Inset for a trailing affordance (clear button, chevron). */
	affordanceInset: string;
	/** Right padding that keeps text clear of a trailing affordance. */
	affordanceClearance: string;
};

/** `Text`'s `body` — the spec's input type scale, shared by both sizes. */
const INPUT_TEXT = "text-[14px] leading-[20px]";

/** Metrics the spec holds constant across sizes. */
const SHARED_METRICS = {
	text: INPUT_TEXT,
	paddingX: "px-[12px]",
	affix: "px-[12px]",
	gutter: "w-[36px]",
	affordanceInset: "right-[12px]",
	affordanceClearance: "pr-[32px]",
} as const;

/** Spec: TextField · Size=Medium (40px). */
const DEFAULT_SIZE: InputSize = {
	...SHARED_METRICS,
	frame: `h-[40px] max-h-[40px] ${INPUT_TEXT}`,
	paddingY: "py-[10px]",
};

/** One step below the spec's Medium (34px), matched to `Button`'s `md`. */
const COMPACT_SIZE: InputSize = {
	...SHARED_METRICS,
	frame: `h-[34px] max-h-[34px] ${INPUT_TEXT}`,
	paddingY: "py-[7px]",
};

export const inputSize = (compact?: boolean): InputSize => (compact ? COMPACT_SIZE : DEFAULT_SIZE);
